import React, { useEffect, useState, useCallback } from 'react';
import { FaInfoCircle, FaPen } from 'react-icons/fa';
import { IoMdAddCircle, IoMdClose } from 'react-icons/io';
import SuperAppButton from '../SuperAppButton';
import { Accordion, AccordionSummary, AccordionDetails, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from "@mui/icons-material/Info";

const CustomToolTip = ({text,direction})=>{
  return(
    <Tooltip placement={direction||undefined} title={text}>
      <InfoIcon />
    </Tooltip>
  )
}

const initialJson = {
  answer: {
    type: 'string',
    description: "Return a JSON object with the key 'search'. If the user does not specify a topic, return 'all' as the search value.",
  },
};

const options = [
  { name: 'type', type: 'option', options: ['string', 'object', 'array'] },
  { name: 'description', type: 'string', value: '' },
];

const setData = (data) => {
  console.log('🚀 ~ setData ~ data:', data);
};

const DynamicJsonEditorV1 = ({
  default_json = initialJson,
  default_options = options,
  default_path = 'properties',
  setListData = setData,
  customTooltip = {},
  fieldData = null,
  formData = null,
  sample_templates = null,
}) => {
  const [jsonData, setJsonData] = useState(null);
  const [primaryPath, setPrimaryPath] = useState(null);
  const [sampleData, setSampleData] = useState({});
  const [optionKeys, setOptionKey] = useState(default_options);
  const [customPath, setCustomPath] = useState(default_path);
  const [expandedAccordions, setExpandedAccordions] = useState({});

  // Trash / Recently Deleted (each entry tracks original path + key + value)
  const [deletedItems, setDeletedItems] = useState([]); // [{id, path, key, value, deletedAt}]

  // ---------- Helpers ----------
  const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

  // Create objects as needed along the path; returns the object at that path
  // Path like: "root.properties.user.properties"
  const ensurePath = (root, path) => {
    if (!path) return root;
    const keys = path.split('.');
    let obj = root;

    for (let i = 0; i < keys.length; i++) {
      const k = keys[i];

      // If something non-object is in the way, replace with object so we can restore
      if (!obj[k] || typeof obj[k] !== 'object' || Array.isArray(obj[k])) {
        obj[k] = {};
      }
      obj = obj[k];
    }
    return obj;
  };

  const uniqueId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  const safeNewKeyName = (obj, base) => {
    if (!Object.prototype.hasOwnProperty.call(obj, base)) return base;
    let i = 1;
    while (Object.prototype.hasOwnProperty.call(obj, `${base}_restored${i}`)) i++;
    return `${base}_restored${i}`;
  };

  // Expand the accordion that owns a container path like "...something.properties"
  // Example: for "foo.properties.bar.properties", we expand "foo.properties.bar"
  const expandForPath = (containerPath) => {
    if (!containerPath) return;
    const parentAccordionKey = containerPath.replace(/\.(properties|items)$/, '');
    setExpandedAccordions((prev) => ({ ...prev, [parentAccordionKey]: true }));
  };

  const sanitizeJsonData = (data) => {
    const sanitized = deepClone(data);
    Object.values(sanitized).forEach((value) => {
      if (value.type === 'array' && !value.items) {
        value.items = { type: 'array', items: { type: 'string' } };
      }
    });
    return sanitized;
  };

  // Initial mount
  useEffect(() => {
    setPrimaryPath(Object.keys(default_json)[0] || null);
    const sanitizedDefaultJson = sanitizeJsonData(default_json);
    setJsonData(sanitizedDefaultJson);
  }, []);

  // Static props initialization
  useEffect(() => {
    if (setListData) {
      const temp = {};
      default_options.forEach((option) => {
        temp[option.name] = option.name === 'description'
          ? 'Return answer on the basis of query'
          : 'string';
      });
      setSampleData(temp);
      setOptionKey(default_options);
      setCustomPath(default_path);
      setListData(jsonData);
    }
  }, [default_options, default_path, jsonData]);

  // Sample templates loading
  useEffect(() => {
    if (formData?.sample_template && sample_templates?.length > 0) {
      const data = sample_templates.find((item) => item?.template_name === formData?.sample_template);
      const newJsonData = data?.[fieldData?.name];
      if (newJsonData && !jsonData) {
        const sanitizedJsonData = sanitizeJsonData(newJsonData);
        setJsonData((prev) => {
          if (JSON.stringify(prev) !== JSON.stringify(sanitizedJsonData)) {
            setListData(sanitizedJsonData);
            return sanitizedJsonData;
          }
          return prev;
        });
      }
    }
  }, [formData, sample_templates, fieldData, sanitizeJsonData, setListData]);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordions((prev) => ({ ...prev, [panel]: isExpanded }));
  };

  const handleKeyChange = (path, oldKey, newKey) => {
    setJsonData((prevData) => {
      const updatedData = deepClone(prevData);
      let obj = updatedData;
      const keys = path ? path.split('.') : [];
      keys.forEach((key) => { if (obj[key]) obj = obj[key]; });

      if (obj && Object.prototype.hasOwnProperty.call(obj, oldKey)) {
        const finalKey = Object.prototype.hasOwnProperty.call(obj, newKey)
          ? safeNewKeyName(obj, newKey)
          : newKey;
        obj[finalKey] = obj[oldKey];
        delete obj[oldKey];
      }
      setListData(updatedData);
      return updatedData;
    });
  };

  const addKeyToObject = (path) => {
    setJsonData((prevData) => {
      const updatedData = deepClone(prevData);
      let obj = updatedData;
      path.split('.').forEach((key) => {
        obj = obj[key] || (obj[key] = {});
      });
      const newKey = `newKey_${Object.keys(obj).length}`;
      obj[newKey] = { ...sampleData };
      setListData(updatedData);
      return updatedData;
    });
  };

  // delete → push to deletedItems with original path+key+value
  const deleteKeyFromObject = (path, keyToDelete) => {
    setJsonData((prevData) => {
      const updatedData = deepClone(prevData);
      let obj = updatedData;
      path.split('.').forEach((key) => { obj = obj[key] || {}; });

      const captured = obj[keyToDelete];
      if (captured !== undefined) {
        setDeletedItems((prev) => [
          {
            id: uniqueId(),
            path,               // original container path
            key: keyToDelete,   // original key
            value: deepClone(captured),
            deletedAt: new Date().toISOString(),
          },
          ...prev,
        ]);
      }

      delete obj[keyToDelete];
      setListData(updatedData);
      return updatedData;
    });
  };

  // ---- RESTORE helpers ----
  const restoreDeletedItem = (id) => {
    const item = deletedItems.find((d) => d.id === id);
    if (!item) return;

    setJsonData((prev) => {
      const updated = deepClone(prev);
      const targetObj = ensurePath(updated, item.path);
      const finalKey = safeNewKeyName(targetObj, item.key);
      targetObj[finalKey] = deepClone(item.value);
      setListData(updated);
      return updated;
    });

    // Open the parent accordion so user sees the restored key
    expandForPath(item.path);

    setDeletedItems((prev) => prev.filter((d) => d.id !== id));
  };

  const restoreByPath = (groupPath) => {
    const group = deletedItems.filter((d) => d.path === groupPath);
    if (group.length === 0) return;

    setJsonData((prev) => {
      const updated = deepClone(prev);
      const targetObj = ensurePath(updated, groupPath);
      group.forEach((item) => {
        const finalKey = safeNewKeyName(targetObj, item.key);
        targetObj[finalKey] = deepClone(item.value);
      });
      setListData(updated);
      return updated;
    });

    expandForPath(groupPath);
    setDeletedItems((prev) => prev.filter((d) => d.path !== groupPath));
  };

  const restoreAllDeleted = () => {
    if (deletedItems.length === 0) return;
    // Group by path to avoid repeated ensurePath
    const byPath = deletedItems.reduce((acc, it) => {
      (acc[it.path] ||= []).push(it);
      return acc;
    }, {});

    setJsonData((prev) => {
      const updated = deepClone(prev);
      Object.entries(byPath).forEach(([path, items]) => {
        const targetObj = ensurePath(updated, path);
        items.forEach((item) => {
          const finalKey = safeNewKeyName(targetObj, item.key);
          targetObj[finalKey] = deepClone(item.value);
        });
      });
      setListData(updated);
      return updated;
    });

    // Expand all parent accordions we restored into
    Object.keys(byPath).forEach(expandForPath);
    setDeletedItems([]);
  };

  const purgeDeletedItem = (id) => setDeletedItems((prev) => prev.filter((d) => d.id !== id));
  const emptyTrash = () => setDeletedItems([]);

  // ----- RenderForm -----
  const RenderForm = React.memo(({ data, path = '', customPath, optionKeys = [] }) => {
    const [editingVal, setEditingVal] = useState(null);
    const [editingKey, setEditingKey] = useState(null);
    const [editingValDescription, setEditingValDescription] = useState(null);
    const [editingKeyDescription, setEditingKeyDescription] = useState(null);
    const [editingItemPath, setEditingItemPath] = useState(null);
    const [tempItemDescription, setTempItemDescription] = useState('');

    const handleTypeChange = (fieldPath, newType, name) => {
      setJsonData((prevData) => {
        let updatedData = deepClone(prevData);
        let obj = updatedData;
        const keys = fieldPath.split('.');

        keys.slice(0, -1).forEach((key) => {
          obj = obj[key] || {};
        });
        const key = keys[keys.length - 1];
        if (newType === 'object') {
          obj[key] = {
            ...obj[key],
            [name]: newType,
            [customPath || 'properties']: { item1: { ...sampleData } },
          };
          delete obj[key].items;
        } else if (newType === 'array') {
          obj[key] = {
            ...obj[key],
            [name]: 'array',
            items: { type: 'array', items: { type: 'string' } },
          };
          delete obj[key][customPath || 'properties'];
        } else {
          obj[key] = { ...obj[key], [name]: newType };
          delete obj[key][customPath || 'properties'];
          delete obj[key].items;
        }
        setListData(updatedData);
        return updatedData;
      });
    };

    const handleFieldChange = (fieldPath, field, value) => {
      setJsonData((prevData) => {
        const updatedData = deepClone(prevData);
        let obj = updatedData;
        const keys = fieldPath.split('.');
        keys.forEach((key, index) => {
          if (index === keys.length - 1) {
            obj[key][field] = value;
          } else {
            obj = obj[key] || {};
          }
        });
        setListData(updatedData);
        return updatedData;
      });
    };

    const handleItemTypeChange = (fieldPath, newType) => {
      setJsonData((prevData) => {
        const updatedData = deepClone(prevData);
        let obj = updatedData;
        fieldPath.split('.').forEach((key) => { obj = obj[key] || {}; });
        if (newType === 'object') {
          obj.items = {
            type: newType,
            properties: { item1: { type: 'string' } },
            description: obj.items?.description || '',
          };
        } else if (newType === 'array') {
          obj.items = {
            type: newType,
            items: { type: 'string' },
            description: obj.items?.description || '',
          };
        } else {
          obj.items = { type: newType, description: obj.items?.description || '' };
        }
        setListData(updatedData);
        return updatedData;
      });
    };

    const handleItemDescriptionChange = (fieldPath, value) => {
      setJsonData((prevData) => {
        const updatedData = deepClone(prevData);
        let obj = updatedData;
        fieldPath.split('.').forEach((key) => { obj = obj[key] || {}; });
        obj.items.description = value;
        setListData(updatedData);
        return updatedData;
      });
    };

    // Local hover tip to avoid MUI Tooltip name clash
    const HoverTip = ({ text, children }) => {
      const [showTooltip, setShowTooltip] = useState(false);
      return (
        <div
          style={{ position: 'relative', display: 'inline-block' }}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          {children}
          {showTooltip && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                background: '#333',
                color: 'white',
                padding: '5px 10px',
                borderRadius: '4px',
                zIndex: 1,
                whiteSpace: 'nowrap',
              }}
            >
              {text}
            </div>
          )}
        </div>
      );
    };

    const RenderToolTip = ({ text }) =>
      text ? (
        <HoverTip text={text}>
          <FaInfoCircle style={{ color: '#2196F3', cursor: 'help' }} />
        </HoverTip>
      ) : null;

    return Object.keys(data).map((key) => {
      const fieldPath = path ? `${path}.${key}` : key;
      const fieldData = data[key];
      const tooltip = customTooltip[path || key] || null;

      if (fieldData.type === 'object') {
        return (
          <Accordion
            key={fieldPath}
            expanded={!!expandedAccordions[fieldPath]}
            onChange={handleAccordionChange(fieldPath)}
            style={{
              marginBottom: '20px',
              backgroundColor: 'transparent',
              color: 'white',
              border: '1px solid #444',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            }}
            defaultExpanded={true}
          >
            <AccordionSummary
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '4px',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.3)' },
              }}
              expandIcon={<ExpandMoreIcon style={{ color: 'white' }} />}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
                {editingKey === fieldPath ? (
                  <>
                    <textarea
                      defaultValue={key}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleKeyChange(path, key, e.target.value);
                        if (e.key === 'Escape') setEditingKey(null);
                      }}
                      onChange={(e) => setEditingVal(e.target.value)}
                      autoFocus
                      style={{
                        padding: '8px',
                        backgroundColor: 'transparent',
                        color: 'white',
                        border: '1px solid #666',
                        borderRadius: '4px',
                        width: '100%',
                        marginBottom: '5px',
                      }}
                    />
                    <button
                      onClick={() => {
                        handleKeyChange(path, key, editingVal);
                        setEditingVal(null);
                        setEditingKey(null);
                      }}
                      style={{
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                    >
                      Save
                    </button>
                  </>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <div>
                      <span style={{ fontSize: '16px', color: '#ddd' }}>
                        {path !== '' ? key : (
                          <>
                            <span style={{marginInline:"10px"}}>Input Parameters</span>
                            <CustomToolTip
                              text="Ensure the input parameter names in the API call are identical for dynamic functionality, as these parameters will also form the basis for the questions asked."
                              direction="top"
                            />
                          </>
                        )}
                      </span>
                      {path !== '' && (
                        <FaPen
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingVal(key);
                            setEditingKey(fieldPath);
                          }}
                          style={{ cursor: 'pointer', color: '#ccc', marginLeft: '5px' }}
                        />
                      )}
                    </div>
                    {path !== '' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteKeyFromObject(path, key);
                        }}
                        className="plus-btn primary-gradient-btn"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "5px",
                          color: "#fff",
                          cursor: 'pointer',
                          background: 'transparent',
                          border: '1px solid #555',
                          borderRadius: 6
                        }}
                        title="Delete (moves to Trash)"
                      >
                        <IoMdClose />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </AccordionSummary>

            <AccordionDetails style={{ padding: '15px', borderTop: '1px solid #444' }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", position:"relative" }}>
                {optionKeys.map((option) => (
                  <div key={option.name}>
                    {option.type === 'option' && (
                      <div style={{ marginBottom: '10px' }}>
                        <div>
                          <label style={{ color: '#bbb', marginRight: '10px' }}>
                            {option.name} <RenderToolTip text={tooltip} />
                          </label>
                          <select
                            value={fieldData[option.name] || 'string'}
                            onChange={(e) => handleTypeChange(fieldPath, e.target.value, option.name)}
                            style={{
                              padding: '8px',
                              backgroundColor: 'transparent',
                              color: 'white',
                              border: '1px solid #666',
                              borderRadius: '4px',
                            }}
                          >
                            {option.options.map((opt) => (
                              <option key={opt} style={{ color: 'black' }} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {option.type === 'string' && (
                      <div style={{ marginBottom: '10px' }}>
                        <label style={{ color: '#bbb', marginRight: '10px' }}>
                          {option.name}
                        </label>
                        {editingKeyDescription === fieldPath && option.name === 'description' ? (
                          <div>
                            <textarea
                              defaultValue={fieldData[option.name] || ''}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleFieldChange(fieldPath, option.name, e.target.value);
                                if (e.key === 'Escape') setEditingValDescription(null);
                              }}
                              onChange={(e) => setEditingValDescription(e.target.value)}
                              autoFocus
                              style={{
                                padding: '8px',
                                backgroundColor: '#333',
                                color: 'white',
                                border: '1px solid #666',
                                borderRadius: '4px',
                                width: '100%',
                                marginBottom: '5px',
                              }}
                            />
                            <button
                              onClick={() => {
                                handleFieldChange(fieldPath, option.name, editingValDescription);
                                setEditingValDescription(null);
                                setEditingKeyDescription(null);
                              }}
                              style={{
                                backgroundColor: '#4CAF50',
                                color: 'white',
                                border: 'none',
                                padding: '8px 12px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                              }}
                            >
                              Save
                            </button>
                          </div>
                        ) : (
                          <div>
                            {fieldData[option.name] || ''}
                            <FaPen
                              onClick={() => {
                                setEditingValDescription(fieldData[option.name] || '');
                                setEditingKeyDescription(fieldPath);
                              }}
                              style={{ cursor: 'pointer', color: '#ccc', marginLeft: '5px' }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <RenderForm
                data={fieldData[customPath || 'properties'] || fieldData}
                path={`${fieldPath}.${customPath || 'properties'}`}
                customPath={customPath || 'properties'}
                optionKeys={optionKeys}
              />

              <span
                onClick={(e) => {
                  e.stopPropagation();
                  addKeyToObject(`${fieldPath}.${customPath || 'properties'}`);
                }}
                style={{ marginInline: 4, fontSize: 12 }}
              >
                <SuperAppButton>
                  <IoMdAddCircle /> Add item
                </SuperAppButton>
              </span>
            </AccordionDetails>
          </Accordion>
        );
      }

      // Primitive or array field editor
      return (
        <div
          key={fieldPath}
          style={{
            marginBottom: '20px',
            padding: '15px',
            border: '1px solid #444',
            borderRadius: '8px',
            backgroundColor: 'transparent',
            color: 'white',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            position:"relative"
          }}
        >
          {path !== '' && (
            <button
              onClick={() => deleteKeyFromObject(path, key)}
              className="plus-btn primary-gradient-btn"
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "5px",
                color: "#fff",
                cursor: 'pointer',
                position:"absolute",
                right:4,
                top:4,
                background: 'transparent',
                border: '1px solid #555',
                borderRadius: 6
              }}
              title="Delete (moves to Trash)"
            >
              <IoMdClose />
            </button>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {editingKey === fieldPath ? (
              <>
                <textarea
                  defaultValue={key}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleKeyChange(path, key, e.target.value);
                    if (e.key === 'Escape') setEditingKey(null);
                  }}
                  onChange={(e) => setEditingVal(e.target.value)}
                  autoFocus
                  style={{
                    padding: '8px',
                    backgroundColor: 'transparent',
                    color: 'white',
                    border: '1px solid #666',
                    borderRadius: '4px',
                    width: '100%',
                    marginBottom: '5px',
                  }}
                />
                <button
                  onClick={() => {
                    handleKeyChange(path, key, editingVal);
                    setEditingVal(null);
                    setEditingKey(null);
                  }}
                  style={{
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  Save
                </button>
              </>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <div>
                  <span style={{ fontSize: '16px', color: '#ddd' }}>{key}</span>
                  {path !== '' && (
                    <FaPen
                      onClick={() => {
                        setEditingVal(key);
                        setEditingKey(fieldPath);
                      }}
                      style={{ cursor: 'pointer', color: '#ccc', marginLeft: '5px' }}
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          {optionKeys.map((option) => (
            <React.Fragment key={option.name}>
              {option.type === 'option' && (
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ color: '#bbb', marginRight: '10px' }}>
                    {option.name}
                  </label>
                  <select
                    value={fieldData[option.name] || 'string'}
                    onChange={(e) => handleTypeChange(fieldPath, e.target.value, option.name)}
                    style={{
                      padding: '8px',
                      backgroundColor: 'transparent',
                      color: 'white',
                      border: '1px solid #666',
                      borderRadius: '4px',
                    }}
                  >
                    {option.options.map((opt) => (
                      <option key={opt} style={{ color: 'black' }} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {option.type === 'string' && (
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ color: '#bbb', marginRight: '10px' }}>
                    {option.name}
                  </label>
                  {editingKeyDescription === fieldPath && option.name === 'description' ? (
                    <div>
                      <textarea
                        defaultValue={fieldData[option.name] || ''}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleFieldChange(fieldPath, option.name, e.target.value);
                          if (e.key === 'Escape') setEditingValDescription(null);
                        }}
                        onChange={(e) => setEditingValDescription(e.target.value)}
                        autoFocus
                        style={{
                          padding: '8px',
                          backgroundColor: '#333',
                          color: 'white',
                          border: '1px solid #666',
                          borderRadius: '4px',
                          width: '100%',
                          marginBottom: '5px',
                        }}
                      />
                      <button
                        onClick={() => {
                          handleFieldChange(fieldPath, option.name, editingValDescription);
                          setEditingValDescription(null);
                          setEditingKeyDescription(null);
                        }}
                        style={{
                          backgroundColor: '#4CAF50',
                          color: 'white',
                          border: 'none',
                          padding: '8px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      >
                        Save
                      </button>
                    </div>
                  ) : (
                    <div>
                      {fieldData[option.name] || ''}
                      <FaPen
                        onClick={() => {
                          setEditingValDescription(fieldData[option.name] || '');
                          setEditingKeyDescription(fieldPath);
                        }}
                        style={{ cursor: 'pointer', color: '#ccc', marginLeft: '5px' }}
                      />
                    </div>
                  )}
                </div>
              )}
            </React.Fragment>
          ))}

          {fieldData.type === 'array' && (
            <div style={{ marginLeft: '20px', marginTop: '10px' }}>
              <h5 style={{ color: '#ccc' }}>Array Item Schema:</h5>
              {typeof fieldData.items === 'object' && !Array.isArray(fieldData.items) ? (
                <div
                  style={{
                    marginBottom: '10px',
                    padding: '10px',
                    border: '1px solid #444',
                    borderRadius: '4px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <label style={{ color: '#bbb', marginRight: '10px' }}>
                      Item Type
                    </label>
                    <select
                      value={fieldData.items.type || 'string'}
                      onChange={(e) => handleItemTypeChange(fieldPath, e.target.value)}
                      style={{
                        padding: '8px',
                        backgroundColor: 'transparent',
                        color: 'white',
                        border: '1px solid #666',
                        borderRadius: '4px',
                      }}
                    >
                      <option style={{ color: 'black' }} value="string">string</option>
                      <option style={{ color: 'black' }} value="number">number</option>
                      <option style={{ color: 'black' }} value="object">object</option>
                      <option style={{ color: 'black' }} value="array">array</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ color: '#bbb', marginRight: '10px' }}>
                      Item Description
                    </label>
                    {editingItemPath === fieldPath ? (
                      <div>
                        <textarea
                          value={tempItemDescription}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleItemDescriptionChange(fieldPath, tempItemDescription);
                              setEditingItemPath(null);
                              setTempItemDescription('');
                            }
                            if (e.key === 'Escape') {
                              setEditingItemPath(null);
                              setTempItemDescription('');
                            }
                          }}
                          onChange={(e) => setTempItemDescription(e.target.value)}
                          autoFocus
                          style={{
                            padding: '8px',
                            backgroundColor: '#333',
                            color: 'white',
                            border: '1px solid #666',
                            borderRadius: '4px',
                            width: '100%',
                            marginBottom: '5px',
                          }}
                        />
                        <button
                          onClick={() => {
                            handleItemDescriptionChange(fieldPath, tempItemDescription);
                            setEditingItemPath(null);
                            setTempItemDescription('');
                          }}
                          style={{
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                          }}
                        >
                          Save
                        </button>
                      </div>
                    ) : (
                      <div>
                        {fieldData.items.description || ''}
                        <FaPen
                          onClick={() => {
                            setEditingItemPath(fieldPath);
                            setTempItemDescription(fieldData.items.description || '');
                          }}
                          style={{ cursor: 'pointer', color: '#ccc', marginLeft: '5px' }}
                        />
                      </div>
                    )}
                  </div>

                  {(fieldData.items.type === 'object' || fieldData.items.type === 'array') && (
                    <RenderForm
                      data={fieldData.items[fieldData.items.type === 'array' ? 'items' : 'properties'] || fieldData.items}
                      path={`${fieldPath}.items.${fieldData.items.type === 'array' ? 'items' : 'properties'}`}
                      customPath={fieldData.items.type === 'array' ? 'items' : 'properties'}
                      optionKeys={optionKeys}
                    />
                  )}
                </div>
              ) : (
                <div style={{ color: '#f44336' }}>
                  Error: Items is not an object
                </div>
              )}
            </div>
          )}
        </div>
      );
    });
  });

  // ------------- Left Sidebar: Trash / Recently Deleted -------------
  const groupedDeleted = deletedItems.reduce((acc, item) => {
    acc[item.path] = acc[item.path] || [];
    acc[item.path].push(item);
    return acc;
  }, {});

  const LeftTrashPanel = () => (
    <div
      style={{
        width: 300,
        minWidth: 300,
        borderRight: '1px solid #2c355a',
        padding: '12px',
        background: 'rgba(11, 23, 60, 0.6)',
        position: 'sticky',
        top: 0,
        height: '100vh',
        overflowY: 'auto',
        color: '#E6ECFF',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Trash / Recently Deleted</h3>
        <div style={{ display: 'flex', gap: 6 }}>
          <SuperAppButton size="small" onClick={restoreAllDeleted} disabled={deletedItems.length === 0}>
            Restore All
          </SuperAppButton>
          <SuperAppButton
            size="small"
            variant="text"
            color="inherit"
            onClick={emptyTrash}
            disabled={deletedItems.length === 0}
          >
            Clear
          </SuperAppButton>
        </div>
      </div>

      {deletedItems.length === 0 ? (
        <div style={{ opacity: 0.7, fontSize: 13 }}>Nothing here yet. Delete a field to see it here.</div>
      ) : (
        Object.keys(groupedDeleted).map((groupPath) => (
          <div
            key={groupPath}
            style={{
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 8,
              padding: 8,
              marginBottom: 10,
              background: 'rgba(255,255,255,0.04)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <div style={{ fontWeight: 700, fontSize: 13, wordBreak: 'break-all' }}>
                Path: <span style={{ opacity: 0.9 }}>{groupPath || '(root)'}</span>
              </div>
              <SuperAppButton size="small" onClick={() => restoreByPath(groupPath)}>
                Restore Group
              </SuperAppButton>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {groupedDeleted[groupPath].map((item) => (
                <div
                  key={item.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto auto',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 8px',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 6,
                    background: 'rgba(255,255,255,0.02)',
                  }}
                >
                  <div style={{ fontSize: 12, wordBreak: 'break-all' }}>
                    <div><strong>Key:</strong> {item.key}</div>
                    <div style={{ opacity: 0.8 }}>
                      <strong>Deleted:</strong> {new Date(item.deletedAt).toLocaleString()}
                    </div>
                  </div>
                  <SuperAppButton size="small" onClick={() => restoreDeletedItem(item.id)}>
                    Restore
                  </SuperAppButton>
                  <SuperAppButton size="small" variant="text" color="inherit" onClick={() => purgeDeletedItem(item.id)}>
                    Remove
                  </SuperAppButton>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );

  // -------------------- Layout with Left Panel + Editor --------------------
  return (
    <div style={{ display: 'flex', backgroundColor: 'transparent', color: 'white' }}>
      {/* Left: Trash Panel */}
      <LeftTrashPanel />

      {/* Right: Editor */}
      <div style={{ flex: 1, padding: '20px' }}>
        {jsonData && (
          <RenderForm
            data={jsonData}
            customPath={customPath}
            optionKeys={optionKeys}
          />
        )}
      </div>
    </div>
  );
};

export default DynamicJsonEditorV1;
